<template>
  <div class="cupons-manager">
    <div class="header-actions">
      <p class="hint">
        Cupons sao codigos de desconto que o inscrito digita no form. <strong>So funcionam em planos a vista (UNICO)</strong>.
      </p>
      <button v-if="!showAdd" type="button" class="btn-primary" @click="abrirAdd" :disabled="loading">+ Novo cupom</button>
    </div>

    <div v-if="showAdd" class="add-form">
      <h4>{{ editing ? 'Editar cupom' : 'Novo cupom' }}</h4>

      <div class="add-grid">
        <div class="form-row">
          <label class="label">Codigo *</label>
          <input class="input code-input" v-model="novo.codigo" maxlength="40"
                 @input="novo.codigo = novo.codigo.toUpperCase().replace(/[^A-Z0-9_-]/g, '')"
                 placeholder="FAMILIA10" />
        </div>
        <div class="form-row">
          <label class="label">Tipo do desconto</label>
          <select class="select" v-model="novo.tipoDesconto">
            <option value="FIXO">Fixo (R$)</option>
            <option value="PERCENTUAL">Percentual (%)</option>
          </select>
        </div>
        <div class="form-row">
          <label class="label">{{ novo.tipoDesconto === 'PERCENTUAL' ? 'Desconto (%)' : 'Desconto (R$)' }}</label>
          <input class="input" type="number" min="0" step="0.01" v-model.number="novo.valorDesconto" />
        </div>
      </div>

      <div v-if="!isOrganizador" class="dist-section">
        <h5 class="dist-title">Distribuicao do desconto</h5>
        <p class="hint small">
          Quanto sai do seu lucro? O resto sai automaticamente do lucro do evento.
        </p>
        <div class="add-grid">
          <div class="form-row">
            <label class="label">Tipo</label>
            <select class="select" v-model="novo.tipoDistribuicao">
              <option value="FIXO">Fixo (R$)</option>
              <option value="PERCENTUAL">Percentual (% do desconto)</option>
            </select>
          </div>
          <div class="form-row">
            <label class="label">{{ novo.tipoDistribuicao === 'PERCENTUAL' ? 'Parcela sua (%)' : 'Parcela sua (R$)' }}</label>
            <input class="input" type="number" min="0" step="0.01" v-model.number="novo.parcelaPlataforma" />
          </div>
        </div>
        <p v-if="previewDist" class="preview">{{ previewDist }}</p>
      </div>
      <div v-else class="organizador-info">
        ℹ️ Como organizador, esse desconto sai 100% do lucro do seu evento.
      </div>

      <div class="add-grid">
        <div class="form-row">
          <label class="label">Valido ate (opcional)</label>
          <input class="input" type="date" v-model="novo.validoAte" />
        </div>
        <div class="form-row">
          <label class="label">Uso maximo total (opcional)</label>
          <input class="input" type="number" min="1" v-model.number="novo.usoMaximo" placeholder="ilimitado" />
        </div>
        <div class="form-row">
          <label class="label">Uso maximo por CPF (opcional)</label>
          <input class="input" type="number" min="1" v-model.number="novo.usoMaximoPorCpf" placeholder="ilimitado" />
        </div>
        <div class="form-row check-row-wrap">
          <label class="check-row">
            <input type="checkbox" v-model="novo.ativo" />
            <span>Ativo</span>
          </label>
        </div>
      </div>

      <div v-if="addError" class="error-msg">{{ addError }}</div>
      <div class="add-actions">
        <button type="button" class="btn-secondary" @click="cancelarAdd">Cancelar</button>
        <button type="button" class="btn-primary" @click="salvar" :disabled="saving">{{ saving ? 'Salvando...' : (editing ? 'Atualizar' : 'Criar') }}</button>
      </div>
    </div>

    <SkeletonLoader v-if="loading" variant="row" :count="3" />

    <ul v-else-if="cupons.length" class="list">
      <li v-for="c in cupons" :key="c.id" class="item" :class="{ inativo: c.status !== 'ATIVO' }">
        <div class="head">
          <strong class="codigo">{{ c.codigo }}</strong>
          <span class="chip" :class="`chip-${c.status.toLowerCase()}`">{{ statusLabel(c.status) }}</span>
          <span v-if="c.criadoPor" class="autor">
            por {{ c.criadoPor.name }} <small>({{ c.criadoPorRole === 'ORGANIZADOR' ? 'organizador' : 'admin' }})</small>
          </span>
        </div>
        <div class="info">
          <span><strong>{{ formatDesconto(c) }}</strong> de desconto</span>
          <span v-if="!isOrganizador" class="muted">
            ·  {{ formatDist(c) }} sai do seu lucro
          </span>
          <span v-if="c.validoAte" class="muted">· ate {{ formatDate(c.validoAte) }}</span>
          <span v-if="c.usoMaximo" class="muted">· {{ c.usos }}/{{ c.usoMaximo }} usos</span>
          <span v-else class="muted">· {{ c.usos }} usos</span>
        </div>
        <div class="actions">
          <button v-if="podeEditar(c)" type="button" class="link-btn" @click="abrirEdit(c)">Editar</button>
          <button v-if="podeEditar(c) && c.usos === 0" type="button" class="link-btn link-danger" @click="remover(c)">Remover</button>
          <button v-else-if="podeEditar(c)" type="button" class="link-btn link-danger" @click="toggleAtivo(c)">{{ c.ativo ? 'Inativar' : 'Ativar' }}</button>
        </div>
      </li>
    </ul>

    <p v-else-if="!showAdd" class="empty">Nenhum cupom criado.</p>
  </div>
</template>

<script>
import api from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import SkeletonLoader from '../ui/SkeletonLoader.vue'

function emptyNovo() {
  return {
    codigo: '',
    tipoDesconto: 'FIXO',
    valorDesconto: 0,
    tipoDistribuicao: 'FIXO',
    parcelaPlataforma: 0,
    validoAte: '',
    usoMaximo: null,
    usoMaximoPorCpf: null,
    ativo: true
  }
}

const STATUS_LABEL = { ATIVO: 'Ativo', INATIVO: 'Inativo', EXPIRADO: 'Expirado', ESGOTADO: 'Esgotado' }

export default {
  name: 'CuponsManager',
  components: { SkeletonLoader },
  props: { eventoId: { type: String, required: true } },
  data() {
    return {
      cupons: [], loading: false,
      showAdd: false, novo: emptyNovo(), saving: false, addError: '',
      editing: null
    }
  },
  computed: {
    authStore() { return useAuthStore() },
    isOrganizador() { return this.authStore.isOrganizador },
    previewDist() {
      const v = Number(this.novo.valorDesconto) || 0
      if (!v) return ''
      const p = Number(this.novo.parcelaPlataforma) || 0
      const example = this.novo.tipoDesconto === 'PERCENTUAL' ? 100 : null
      let descontoBruto, parcela
      if (this.novo.tipoDesconto === 'PERCENTUAL') {
        descontoBruto = example * v / 100
      } else {
        descontoBruto = v
      }
      if (this.novo.tipoDistribuicao === 'PERCENTUAL') {
        parcela = descontoBruto * p / 100
      } else {
        parcela = p
      }
      const parcelaEvento = Math.max(0, descontoBruto - parcela)
      if (this.novo.tipoDesconto === 'PERCENTUAL') {
        return `Ex: numa inscricao de R$ 100, desconto R$ ${descontoBruto.toFixed(2)} = R$ ${parcela.toFixed(2)} seu + R$ ${parcelaEvento.toFixed(2)} do evento.`
      }
      return `Desconto R$ ${descontoBruto.toFixed(2)} = R$ ${parcela.toFixed(2)} seu + R$ ${parcelaEvento.toFixed(2)} do evento.`
    }
  },
  async mounted() { await this.load() },
  methods: {
    statusLabel(s) { return STATUS_LABEL[s] || s },
    formatDate(d) { try { return new Date(d).toLocaleDateString('pt-BR') } catch { return d } },
    formatDesconto(c) {
      if (c.tipoDesconto === 'PERCENTUAL') return `${c.valorDesconto}%`
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(c.valorDesconto)
    },
    formatDist(c) {
      if (c.tipoDistribuicao === 'PERCENTUAL') return `${c.parcelaPlataforma}% do desconto`
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(c.parcelaPlataforma)
    },
    podeEditar(c) {
      if (!this.authStore.canManageEventos && !this.isOrganizador) return false
      if (this.isOrganizador) return c.criadoPorId === this.authStore.admin?.id
      return true
    },
    async load() {
      this.loading = true
      try {
        const { data } = await api.get('/cupons', { params: { eventoId: this.eventoId } })
        this.cupons = data.cupons
      } finally { this.loading = false }
    },
    abrirAdd() {
      this.editing = null
      this.novo = emptyNovo()
      this.addError = ''
      this.showAdd = true
    },
    abrirEdit(c) {
      this.editing = c
      this.novo = {
        codigo: c.codigo,
        tipoDesconto: c.tipoDesconto,
        valorDesconto: c.valorDesconto,
        tipoDistribuicao: c.tipoDistribuicao,
        parcelaPlataforma: c.parcelaPlataforma,
        validoAte: c.validoAte ? new Date(c.validoAte).toISOString().slice(0, 10) : '',
        usoMaximo: c.usoMaximo,
        usoMaximoPorCpf: c.usoMaximoPorCpf,
        ativo: c.ativo
      }
      this.addError = ''
      this.showAdd = true
    },
    cancelarAdd() {
      this.showAdd = false
      this.editing = null
      this.addError = ''
    },
    async salvar() {
      this.saving = true
      this.addError = ''
      try {
        const payload = {
          codigo: this.novo.codigo,
          tipoDesconto: this.novo.tipoDesconto,
          valorDesconto: Number(this.novo.valorDesconto),
          tipoDistribuicao: this.novo.tipoDistribuicao,
          parcelaPlataforma: Number(this.novo.parcelaPlataforma) || 0,
          validoAte: this.novo.validoAte || null,
          usoMaximo: this.novo.usoMaximo || null,
          usoMaximoPorCpf: this.novo.usoMaximoPorCpf || null,
          ativo: this.novo.ativo
        }
        if (this.editing) {
          await api.patch(`/cupons/${this.editing.id}`, payload)
        } else {
          await api.post('/cupons', { ...payload, eventoId: this.eventoId })
        }
        await this.load()
        this.cancelarAdd()
      } catch (err) {
        const data = err.response?.data
        let msg = data?.message || 'Erro'
        if (Array.isArray(data?.errors) && data.errors.length) {
          msg += ': ' + data.errors.map(e => `${e.field} - ${e.message}`).join('; ')
        }
        this.addError = msg
      } finally {
        this.saving = false
      }
    },
    async toggleAtivo(c) {
      try {
        await api.patch(`/cupons/${c.id}`, { ativo: !c.ativo })
        await this.load()
      } catch (err) {
        this.$toast.open({ message: err.response?.data?.message || 'Erro', type: 'error', position: 'top-right' })
      }
    },
    async remover(c) {
      if (!confirm(`Remover cupom "${c.codigo}"?`)) return
      try {
        await api.delete(`/cupons/${c.id}`)
        this.cupons = this.cupons.filter(x => x.id !== c.id)
      } catch (err) {
        this.$toast.open({ message: err.response?.data?.message || 'Erro', type: 'error', position: 'top-right' })
      }
    }
  }
}
</script>

<style scoped>
.cupons-manager { display: flex; flex-direction: column; gap: var(--spacing-base); }
.header-actions { display: flex; align-items: center; justify-content: space-between; gap: var(--spacing-md); }
.hint { color: var(--color-text-muted); font-size: var(--text-sm); margin: 0; flex: 1; }
.hint.small { font-size: var(--text-xs); margin-bottom: var(--spacing-sm); }
.add-form { border: 1px dashed var(--color-bg-separator); border-radius: var(--radius-md); padding: var(--spacing-base); display: flex; flex-direction: column; gap: var(--spacing-md); }
.add-form h4 { font-size: var(--text-sm); }
.add-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: var(--spacing-sm); }
.form-row { display: flex; flex-direction: column; gap: 4px; }
.check-row-wrap { justify-content: flex-end; }
.check-row { display: flex; align-items: center; gap: 6px; font-size: var(--text-sm); cursor: pointer; }
.code-input { text-transform: uppercase; font-family: monospace; }
.dist-section { padding: var(--spacing-md); background: var(--color-bg-hover); border-radius: var(--radius-md); }
.dist-title { font-size: var(--text-xs); text-transform: uppercase; letter-spacing: 0.5px; color: var(--color-text-heading); margin-bottom: var(--spacing-xs); }
.preview { font-size: var(--text-xs); color: var(--color-text-secondary); margin-top: var(--spacing-sm); padding: var(--spacing-sm); background: rgba(255,255,255,0.6); border-radius: var(--radius-sm); }
.organizador-info { padding: var(--spacing-sm) var(--spacing-md); background: #ede9fe; color: #6d28d9; border-radius: var(--radius-md); font-size: var(--text-sm); }
.add-actions { display: flex; gap: var(--spacing-sm); justify-content: flex-end; }
.list { list-style: none; display: flex; flex-direction: column; gap: var(--spacing-sm); }
.item { padding: var(--spacing-sm) var(--spacing-md); background: var(--color-bg-hover); border-radius: var(--radius-md); display: flex; flex-direction: column; gap: 4px; }
.item.inativo { opacity: 0.55; }
.head { display: flex; align-items: center; gap: var(--spacing-sm); flex-wrap: wrap; }
.codigo { font-family: monospace; font-size: var(--text-md); }
.chip { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; padding: 2px 6px; border-radius: 3px; }
.chip-ativo { background: #dcfce7; color: #166534; }
.chip-inativo { background: var(--color-bg-body); color: var(--color-text-muted); }
.chip-expirado { background: #fef3c7; color: #92400e; }
.chip-esgotado { background: #fee2e2; color: #b91c1c; }
.autor { font-size: 11px; color: var(--color-text-muted); margin-left: auto; }
.info { font-size: var(--text-sm); display: flex; flex-wrap: wrap; gap: 4px; }
.muted { color: var(--color-text-muted); }
.actions { display: flex; gap: 4px; margin-top: 4px; }
.link-btn { background: none; border: none; cursor: pointer; color: var(--color-accent); font-size: var(--text-xs); padding: 2px 6px; }
.link-danger { color: var(--color-danger); }
.empty { color: var(--color-text-muted); font-size: var(--text-sm); text-align: center; padding: var(--spacing-base); }
</style>
